/**
 * Deduplicated Network Error Logger
 *
 * Prevents duplicate Sentry/console entries when multiple hooks
 * independently log the same underlying network error (e.g., "Failed to fetch").
 *
 * Problem: A single fetch failure generates 3-4 separate log entries from
 * useEntitlements, useTier, useQuota, prewarm, useAnalyze, and useSubscription
 * because they all fire Supabase RPCs that fail simultaneously.
 *
 * Solution: Deduplicate by endpoint+error within a configurable time window.
 * The first occurrence logs immediately; subsequent duplicates within the
 * window are silently dropped and counted. When the window expires, a
 * summary is logged if duplicates were suppressed.
 *
 * Task: NocoDB #1606
 * Created: 2026-03-19
 *
 * Sentry Integration (NocoDB #2445, 2026-04-01):
 * Non-transient errors (4xx, business logic) are reported to Sentry via
 * captureError(). Transient network errors (Failed to fetch, timeout) are
 * only logged to console to avoid noise.
 */

import { captureError } from './analytics';
import { isApiError, isAuthRequiredError } from '@dramadetector/api-client';

/** How long (ms) to suppress duplicate transient network errors */
const DEDUP_WINDOW_MS = 10_000; // 10 seconds

/** How long (ms) to suppress duplicate non-transient errors (Sentry dedup).
 *  Shorter window — just enough to collapse React Query's 3 retries into one
 *  Sentry event per failure cycle. */
const SENTRY_DEDUP_WINDOW_MS = 5_000; // 5 seconds

interface DedupEntry {
  /** Timestamp of the first occurrence */
  firstSeen: number;
  /** Number of suppressed duplicates */
  count: number;
  /** All hooks that reported this error (for summary) */
  hooks: Set<string>;
  /** Timer for summary log */
  timer: ReturnType<typeof setTimeout> | null;
  /** Cached error message for summary flush */
  message: string;
  /** Cached endpoint for summary flush */
  endpoint: string;
}

/**
 * Map of "endpoint|errorMessage" -> dedup entry.
 * Entries are auto-cleaned when their window expires.
 */
const dedupMap = new Map<string, DedupEntry>();

export interface NetworkErrorContext {
  /** Name of the hook/module reporting the error (e.g., "useEntitlements") */
  hook: string;
  /** The endpoint or RPC name that failed (e.g., "get_user_entitlements") */
  endpoint: string;
  /** Whether the user is authenticated */
  isAuthenticated?: boolean;
  /** Additional context for the log entry */
  extra?: Record<string, unknown>;
}

/**
 * Build a dedup key from the endpoint and error message.
 * Normalizes common network error messages so slight variations
 * (e.g., different error object shapes) still deduplicate.
 */
function buildKey(endpoint: string, error: unknown): string {
  const msg = extractMessage(error);
  return `${endpoint}|${msg}`;
}

/**
 * Extract a stable error message string from various error shapes.
 */
function extractMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>;
    if (typeof e.message === 'string') return e.message;
    if (typeof e.error === 'string') return e.error;
  }
  return String(error);
}

/**
 * Check if an error is a transient network error that benefits from dedup.
 * Non-network errors (e.g., RLS violations, 4xx responses) are always logged.
 */
function isNetworkError(error: unknown): boolean {
  if (isApiError(error) && error.code === 'NETWORK_ERROR') {
    return true;
  }

  const msg = extractMessage(error).toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('network request failed') ||
    msg.includes('networkerror') ||
    msg.includes('load failed') ||
    msg.includes('timeout') ||
    msg.includes('err_internet_disconnected') ||
    msg.includes('err_network') ||
    msg.includes('request aborted') ||
    msg.includes('the operation was aborted')
  );
}

/**
 * Flush a dedup entry's suppressed-error summary to console.warn.
 * Called before clearing a timer to avoid losing counted duplicates.
 */
function flushSummary(entry: DedupEntry): void {
  if (entry.count > 0) {
    const hookList = Array.from(entry.hooks).join(', ');
    // Use console.debug (not console.warn) so Sentry breadcrumb capture
    // doesn't surface dedup summaries as noise. The first occurrence is
    // already logged as console.error — this summary is informational only.
    console.debug(
      `[logNetworkError] Suppressed ${entry.count} duplicate "${entry.message}" ` +
      `errors on ${entry.endpoint} from: ${hookList}`
    );
  }
}

/**
 * Log a network error with deduplication.
 *
 * If the same endpoint+error combination was already logged within the
 * dedup window, the call is silently counted. Otherwise, the error is
 * logged immediately via console.error.
 *
 * Non-network errors (business logic errors, 4xx, etc.) always pass through
 * without deduplication to preserve debugging visibility.
 *
 * @param error - The error object or message
 * @param context - Hook name, endpoint, and optional metadata
 */
export function logNetworkError(error: unknown, context: NetworkErrorContext): void {
  const { hook, endpoint, isAuthenticated, extra } = context;

  // Drop auth-required rejections for unauthenticated callers. These are
  // routing failures (the caller should have gated on auth state first), not
  // bugs to capture in Sentry. Tracks DRAMA-WEB-2S. The deeper fix — preventing
  // unauthenticated callers from invoking authed endpoints in the first place
  // — lives in the calling hook (e.g. useAnalyze gating on user.id).
  if (isAuthenticated === false && isAuthRequiredError(error)) {
    console.debug(
      `[${hook}] Auth-required error suppressed (caller is anonymous): ${endpoint}`,
      extra ? extra : ''
    );
    return;
  }

  // Non-network errors: log + report to Sentry with dedup to collapse React
  // Query retries (3 retries = 4 calls) into a single Sentry event.
  if (!isNetworkError(error)) {
    const key = buildKey(endpoint, error);
    const now = Date.now();
    const existing = dedupMap.get(key);

    if (existing && now - existing.firstSeen < SENTRY_DEDUP_WINDOW_MS) {
      // Within Sentry dedup window: suppress retry noise
      existing.count += 1;
      existing.hooks.add(hook);
      return;
    }

    console.error(`[${hook}] ${endpoint} error:`, error, extra ? extra : '');
    captureError(
      error instanceof Error ? error : new Error(extractMessage(error)),
      { hook, endpoint, isAuthenticated, ...extra },
    );

    // Start dedup window for this error
    if (existing?.timer) clearTimeout(existing.timer);
    dedupMap.set(key, {
      firstSeen: now,
      count: 0,
      hooks: new Set([hook]),
      timer: setTimeout(() => { const e = dedupMap.get(key); if (e) { flushSummary(e); dedupMap.delete(key); } }, SENTRY_DEDUP_WINDOW_MS),
      message: extractMessage(error),
      endpoint,
    });
    return;
  }

  const key = buildKey(endpoint, error);
  const now = Date.now();
  const existing = dedupMap.get(key);

  if (existing && now - existing.firstSeen < DEDUP_WINDOW_MS) {
    // Within dedup window: suppress and count
    existing.count += 1;
    existing.hooks.add(hook);
    return;
  }

  // First occurrence or window expired: log and start new entry
  const authLabel = isAuthenticated === undefined ? '' : isAuthenticated ? ' (authenticated)' : ' (anonymous)';
  console.error(
    `[${hook}] Network error on ${endpoint}${authLabel}:`,
    extractMessage(error),
    extra ? extra : ''
  );

  // Flush pending summary before clearing the old timer to avoid losing counts.
  // This handles the race where a new error arrives just after the window expires
  // but before the timer callback fires.
  if (existing) {
    flushSummary(existing);
    if (existing.timer) {
      clearTimeout(existing.timer);
    }
  }

  const msg = extractMessage(error);
  const entry: DedupEntry = {
    firstSeen: now,
    count: 0,
    hooks: new Set([hook]),
    timer: null,
    message: msg,
    endpoint,
  };

  // Schedule summary log when window expires
  entry.timer = setTimeout(() => {
    const final = dedupMap.get(key);
    if (final) {
      flushSummary(final);
    }
    dedupMap.delete(key);
  }, DEDUP_WINDOW_MS);

  dedupMap.set(key, entry);
}

/**
 * Reset the dedup state. Useful for testing.
 */
export function resetNetworkErrorDedup(): void {
  for (const entry of dedupMap.values()) {
    if (entry.timer) clearTimeout(entry.timer);
  }
  dedupMap.clear();
}
